import { animate, style, transition, trigger } from '@angular/animations';
import {
  AfterViewInit,
  Component,
  inject,
  Injector,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import {
  FormControl,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import {
  EVENT_TOUR_STEPS,
  jumpToSampleEvent,
  showLiveEventTour,
} from './live-event-tour';
import { EXAM_TOUR_STEPS, jumpToSampleExam, showExamTour } from './exam-tour';
import {
  COURSE_TOUR_STEPS,
  jumpToSampleCourse,
  showCourseTour,
} from './course-tour';
import { UsersService } from 'src/app/api/services';
import { AuthService } from 'src/app/auth/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { UserConfigService } from '../../user-config.service';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { NgTemplateOutlet } from '@angular/common';
import { ButtonComponent } from 'src/app/shared/components/button/button.component';
import { DialogService } from '../../common/dialog.service';
import { environment } from 'src/environments/environment';
import { SelectionListComponent } from 'src/app/shared/components/selection-list/selection-list.component';
import { getNestedValue } from 'src/app/utility/app.utils';
import { TrackingService } from 'src/app/utility/amplitude-tracking/tracking.service';
import { UIEventEnum } from 'src/app/utility/amplitude-tracking/tracking.types';
import { PanelModule } from '../../panel/panel.module';
import { QuickFeedbackOverlayService } from '../../common/quick-feedback-overlay/quick-feedback-overlay.service';
import { CancelFeedbackDialogData } from 'src/app/profile/subscription/cancel-feedback/cancel-feedback.component';
import { firstValueFrom } from 'rxjs';
import { MatInputModule } from '@angular/material/input';
import { ExamCardComponent } from '../dashboard/exam-card/exam-card.component';
import { LiveEventCardComponent } from '../dashboard/live-event-card/live-event-card.component';
import { CourseCardComponent } from '../dashboard/course-card/course-card.component';
import {
  ProgressCard,
  ProgressCardComponent,
} from 'src/app/profile/progress/progress-card/progress-card.component';
import { TourTrackType } from './guided-tour';
import { getSteps } from '../dashboard/dashboard-card-base.component';
import { UserJourneyService } from '../onboarding-steps/user-journey.service';

export interface OnboardingDialogData {
  skipped: boolean;
}

interface OnboardingStep {
  title: string;
  text?: string;
  name: string;
  image?: string;
}

interface EditionText {
  school: string;
  go: string;
  university: string;
}

interface UseCase {
  title: EditionText;
  description: EditionText;
  buttonText: EditionText;
  amplitudeValue: string;
  icon: string;
  onActivate: () => void;
  skipTour?: () => void;
}

interface RoleOption {
  amplitudeValue: string;
  role: string;
}

interface SourceOption {
  amplitudeValue: string;
  source: string;
}

interface GoalOption {
  amplitudeValue: string;
  goal: string;
}

export enum OnboardingSteps {
  ROLE,
  WRONG_REGISTRATION,
  PHONE_NUMBER,
  SOURCE,
  USE_CASES,
  TOUR,
}

@Component({
  selector: 'qa-onboarding',
  templateUrl: './onboarding.component.html',
  styleUrl: './onboarding.component.scss',
  animations: [
    trigger('pageTransition', [
      transition('* => *', [
        style({ opacity: 0, transform: 'scale(0.4)' }),
        animate(
          '300ms ease-in-out',
          style({ opacity: 1, transform: 'scale(1)' })
        ),
      ]),
    ]),
  ],
  imports: [
    NgTemplateOutlet,
    NgScrollbarModule,
    TranslateModule,
    ButtonComponent,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    SelectionListComponent,
    PanelModule,
    ExamCardComponent,
    LiveEventCardComponent,
    CourseCardComponent,
    ProgressCardComponent,
  ],
})
export class OnboardingComponent implements OnInit, OnDestroy, AfterViewInit {
  private userJourney = inject(UserJourneyService);

  currentStep = 0;
  OnboardingSteps = OnboardingSteps;

  edition = environment.edition as 'school' | 'go' | 'university' | 'dev';

  phoneNumberControl = new FormControl('', [
    Validators.pattern(/^((\+|00)(49|43|41)|0)[1-9][0-9\s\-\/()]{3,}$/),
  ]);

  steps: OnboardingStep[] = [];
  cardData: ProgressCard;

  courseUseCase: UseCase = {
    title: {
      school: 'onboarding.steps.quickStart.course',
      go: 'onboarding.steps.quickStart.course',
      university: 'onboarding.steps.quickStart.course',
    },
    description: {
      school: 'onboarding.steps.quickStart.school.courseExplanation',
      go: 'onboarding.steps.quickStart.go.courseExplanation',
      university: 'onboarding.steps.quickStart.university.courseExplanation',
    },
    buttonText: {
      school: 'onboarding.steps.quickStart.discoverCourse',
      go: 'onboarding.steps.quickStart.discoverCourse',
      university: 'onboarding.steps.quickStart.discoverCourse',
    },
    icon: 'collections_bookmark',
    amplitudeValue: 'course',
    onActivate: () => {
      showCourseTour(this.injector, COURSE_TOUR_STEPS.SHARE_COURSE);
    },
    skipTour: () => {
      jumpToSampleCourse(this.injector);
    },
  };

  liveEventUseCase: UseCase = {
    title: {
      school: 'onboarding.steps.quickStart.liveEvent',
      go: 'onboarding.steps.quickStart.liveEvent',
      university: 'onboarding.steps.quickStart.liveEvent',
    },
    description: {
      school: 'onboarding.steps.quickStart.school.liveEventExplanation',
      go: 'onboarding.steps.quickStart.go.liveEventExplanation',
      university: 'onboarding.steps.quickStart.university.liveEventExplanation',
    },
    buttonText: {
      school: 'onboarding.steps.quickStart.discoverLiveEvent',
      go: 'onboarding.steps.quickStart.discoverLiveEvent',
      university: 'onboarding.steps.quickStart.discoverLiveEvent',
    },
    icon: 'live_tv',
    amplitudeValue: 'liveEvent',
    onActivate: () => {
      showLiveEventTour(this.injector, EVENT_TOUR_STEPS.SHARE_EVENT);
    },
    skipTour: () => {
      jumpToSampleEvent(this.injector);
    },
  };

  examUseCase: UseCase = {
    title: {
      school: 'onboarding.steps.quickStart.exam',
      go: 'onboarding.steps.quickStart.exam',
      university: 'onboarding.steps.quickStart.exam',
    },
    description: {
      school: 'onboarding.steps.quickStart.school.examExplanation',
      go: 'onboarding.steps.quickStart.go.examExplanation',
      university: 'onboarding.steps.quickStart.university.examExplanation',
    },
    buttonText: {
      school: 'onboarding.steps.quickStart.discoverExam',
      go: 'onboarding.steps.quickStart.discoverExam',
      university: 'onboarding.steps.quickStart.discoverExam',
    },
    icon: 'assignment',
    amplitudeValue: 'exam',
    onActivate: () => {
      showExamTour(this.injector, EXAM_TOUR_STEPS.SHARE_SECTION);
    },
    skipTour: () => {
      jumpToSampleExam(this.injector);
    },
  };

  roleOptions: { [key: string]: RoleOption[] } = {
    school: [
      {
        amplitudeValue: 'teacher',
        role: 'onboarding.steps.welcome.school.teacher',
      },
      {
        amplitudeValue: 'administrator',
        role: 'onboarding.steps.welcome.school.administrator',
      },
      {
        amplitudeValue: 'student',
        role: 'onboarding.steps.welcome.school.student',
      },
      {
        amplitudeValue: 'other',
        role: 'onboarding.steps.welcome.school.other',
      },
    ],
    go: [
      {
        amplitudeValue: 'trainingCoordinator',
        role: 'onboarding.steps.welcome.go.trainingCoordinator',
      },
      {
        amplitudeValue: 'trainer',
        role: 'onboarding.steps.welcome.go.trainer',
      },
      {
        amplitudeValue: 'administrator',
        role: 'onboarding.steps.welcome.go.administrator',
      },
      {
        amplitudeValue: 'learner',
        role: 'onboarding.steps.welcome.go.learner',
      },
      {
        amplitudeValue: 'privatePerson',
        role: 'onboarding.steps.welcome.go.privatePerson',
      },
      { amplitudeValue: 'other', role: 'onboarding.steps.welcome.go.other' },
    ],
    university: [
      {
        amplitudeValue: 'lecturer',
        role: 'onboarding.steps.welcome.university.lecturer',
      },
      {
        amplitudeValue: 'administrator',
        role: 'onboarding.steps.welcome.university.administrator',
      },
      {
        amplitudeValue: 'collegeStudent',
        role: 'onboarding.steps.welcome.university.collegeStudent',
      },
      {
        amplitudeValue: 'other',
        role: 'onboarding.steps.welcome.university.other',
      },
    ],
  };

  qaKnowledgeSourceOptions: SourceOption[] = [
    {
      amplitudeValue: 'searchEngine',
      source: 'onboarding.steps.qaKnowledgeSource.options.searchEngine',
    },
    {
      amplitudeValue: 'aiBasedSearch',
      source: 'onboarding.steps.qaKnowledgeSource.options.aiBasedSearch',
    },
    {
      amplitudeValue: 'recommendation',
      source: 'onboarding.steps.qaKnowledgeSource.options.recommendation',
    },
    {
      amplitudeValue: 'education',
      source: 'onboarding.steps.qaKnowledgeSource.options.education',
    },
    {
      amplitudeValue: 'socialMedia',
      source: 'onboarding.steps.qaKnowledgeSource.options.socialMedia',
    },
    {
      amplitudeValue: 'event',
      source: 'onboarding.steps.qaKnowledgeSource.options.event',
    },
    {
      amplitudeValue: 'alreadyUsed',
      source: 'onboarding.steps.qaKnowledgeSource.options.alreadyUsed',
    },
    {
      amplitudeValue: 'other',
      source: 'onboarding.steps.qaKnowledgeSource.options.other',
    },
  ];

  qaGoalsOptions: GoalOption[] = [
    {
      amplitudeValue: 'interactiveLessons',
      goal: 'onboarding.steps.qaGoals.options.interactiveLessons',
    },
    {
      amplitudeValue: 'provideContent',
      goal: 'onboarding.steps.qaGoals.options.provideContent',
    },
    {
      amplitudeValue: 'evaluatePerformance',
      goal: 'onboarding.steps.qaGoals.options.evaluatePerformance',
    },
    { amplitudeValue: 'other', goal: 'onboarding.steps.qaGoals.options.other' },
  ];

  switchingStep = false;
  germanTranslations;
  stepsSelectValues = {
    firstStep: {
      id: 'role',
      selectedValue: null,
      isCustomValue: false,
      customValue: null,
    },
    secondStep: {
      id: 'knowledge_source',
      selectedValue: null,
      isCustomValue: false,
      customValue: null,
    },
    thirdStep: {
      id: 'goals',
      selectedValue: [],
      isCustomValue: false,
      customValue: null,
    },
    fourthStep: {
      id: 'selected_use_case',
      selectedValue: null,
    },
  };

  constructor(
    private feedback: QuickFeedbackOverlayService,
    private dialogRef: MatDialogRef<OnboardingComponent>,
    private injector: Injector,
    private usersService: UsersService,
    private auth: AuthService,
    private snackBar: MatSnackBar,
    private translator: TranslateService,
    private userConfig: UserConfigService,
    private dialog: DialogService,
    private trackingService: TrackingService,
    private renderer: Renderer2
  ) {
    if (this.edition === 'dev') {
      this.edition = 'school';
    }
    this.setStepsInfo();
  }

  ngOnInit(): void {
    this.renderer.addClass(document.body, 'onboarding-visible');
  }

  ngOnDestroy(): void {
    this.renderer.removeClass(document.body, 'onboarding-visible');
  }

  ngAfterViewInit(): void {
    this.getGermanTranslations();
  }

  getGermanTranslations() {
    this.translator.getTranslation('de').subscribe((translations) => {
      this.germanTranslations = translations;
    });
  }

  setStepsInfo() {
    this.steps = [
      {
        title: 'onboarding.steps.welcome.title',
        text: 'onboarding.steps.welcome.text',
        name: 'onboarding.steps.welcome.name',
      },
      {
        title: `onboarding.steps.wrongRegistered.${this.edition}.title`,
        text: 'onboarding.steps.wrongRegistered.text',
        name: 'onboarding.steps.wrongRegistered.name',
      },
      {
        title: 'onboarding.steps.additionalInfo.title',
        text: 'onboarding.steps.additionalInfo.text',
        name: 'onboarding.steps.additionalInfo.name',
      },
      {
        title: 'onboarding.steps.qaKnowledgeSource.title',
        text: 'onboarding.steps.qaKnowledgeSource.text',
        name: 'onboarding.steps.qaKnowledgeSource.name',
      },
      // Hide step temporary
      // {
      //   title: 'onboarding.steps.qaGoals.title',
      //   text: 'onboarding.steps.qaGoals.text',
      //   name: 'onboarding.steps.qaGoals.name',
      // },
      {
        title: 'onboarding.steps.quickStart.title',
        name: 'onboarding.steps.quickStart.name',
      },
      {
        image: 'assets/images/Beispielgrafik.png',
        title: `onboarding.steps.guidedTour.${this.stepsSelectValues.fourthStep.selectedValue?.amplitudeValue}.title`,
        text: `onboarding.steps.guidedTour.${this.stepsSelectValues.fourthStep.selectedValue?.amplitudeValue}.text`,
        name: 'onboarding.steps.guidedTour.name',
      },
    ];
  }

  updateIsCustomValue(step: string, value: string) {
    this.stepsSelectValues[step].isCustomValue =
      value.includes('.misc') || value.includes('.other');
  }

  onRoleSubmit() {
    let selectedValue = this.stepsSelectValues.firstStep.selectedValue.role;
    if (this.stepsSelectValues.firstStep.isCustomValue) {
      const customValue = this.stepsSelectValues?.firstStep?.customValue
        ? this.stepsSelectValues.firstStep.customValue.trim()
        : '';
      selectedValue = `Sonstiges (${customValue})`;
    } else {
      selectedValue = getNestedValue(this.germanTranslations, selectedValue);
    }
    this.switchingStep = true;
    this.userConfig.set('onboarding.role', selectedValue).then(() => {
      this.nextStep(this.isWrongRegister ? 1 : 2);
    });
  }

  async onDeleteAccount() {
    await this.handleCRMTracking();
    this.handleAmplitudeTracking();
    this.logout();
  }

  async handleCRMTracking() {
    const config = {
      ...this.userConfig.getUserConfig(),
      deleteRequestFeedback: {
        feedback: '',
        reason: 'Ich habe mich versehentlich als Lernender registriert.',
        satisfaction: '',
      },
    };
    const userId = this.auth.currentUserInfo$.value.sub;
    return firstValueFrom(
      this.usersService.updateUser({
        id: userId,
        body: { id: userId, config },
      })
    );
  }

  handleAmplitudeTracking() {
    const amplitudeValues = {
      edition: this.edition,
      feedback: '',
      reason: 'wrongRegistration',
      satisfaction: '',
    };
    const feedback: CancelFeedbackDialogData = {
      skipped: false,
      deletionReason: 'wrongRegistration',
      amplitudeValues,
      submittedOn: new Date().toISOString(),
    };
    this.feedback.requestAccountDelete(feedback, null);
  }

  logout() {
    this.dialogRef.close({ skipped: true });
    this.auth.logout();
  }

  onQaKnowledgeSourceSubmit() {
    let selectedValue = this.stepsSelectValues.secondStep.selectedValue.source;
    if (this.stepsSelectValues.secondStep.isCustomValue) {
      const customValue = this.stepsSelectValues?.secondStep?.customValue
        ? this.stepsSelectValues.secondStep.customValue.trim()
        : '';
      selectedValue = `Sonstiges (${customValue})`;
    } else {
      selectedValue = getNestedValue(this.germanTranslations, selectedValue);
    }
    this.switchingStep = true;
    this.userConfig
      .set('onboarding.qaKnowledgeSource', selectedValue)
      .then(() => {
        this.nextStep();
      });
  }

  onSelectQaGoals(event: any, option: GoalOption, index: number) {
    if (this.qaGoalsOptions.length - 1 === index) {
      this.stepsSelectValues.thirdStep.isCustomValue = event.checked;
    }
    if (event.checked) {
      this.stepsSelectValues.thirdStep.selectedValue.push(option);
    } else {
      this.stepsSelectValues.thirdStep.selectedValue =
        this.stepsSelectValues.thirdStep.selectedValue.filter(
          (val) => val?.goal !== option?.goal
        );
    }
  }

  getIsGoalSelected(option: GoalOption): boolean {
    return !!this.stepsSelectValues.thirdStep.selectedValue.find(
      (value) => option?.goal === value?.goal
    );
  }

  onQaGoals() {
    const finalSelections: string[] = [
      ...this.stepsSelectValues.thirdStep.selectedValue.map((value) =>
        getNestedValue(this.germanTranslations, value.goal)
      ),
    ];
    if (this.stepsSelectValues.thirdStep.isCustomValue) {
      const customValue = this.stepsSelectValues?.thirdStep?.customValue
        ? this.stepsSelectValues.thirdStep.customValue.trim()
        : '';
      finalSelections.push(`Sonstiges (${customValue})`);
    }
    const result = finalSelections.join('; ');
    this.switchingStep = true;
    this.userConfig.set('onboarding.qaGoals', result).then(() => {
      this.nextStep();
    });
  }

  nextStep(step = 1) {
    this.switchingStep = true;
    if (this.currentStep === this.steps.length - 1) {
      this.dialogRef.close(false);
      return;
    }
    this.currentStep += step;
    this.switchingStep = false;
  }

  previousStep() {
    this.switchingStep = true;
    if (this.currentStep > 0) {
      if (this.currentStep === 2 && !this.isWrongRegister) {
        this.currentStep--;
      }
      this.currentStep--;
      this.switchingStep = false;
    }
  }

  get isWrongRegister(): boolean {
    const selectedValue =
      this.stepsSelectValues.firstStep.selectedValue?.amplitudeValue;
    const isLearner = ['student', 'learner', 'collegeStudent'].includes(
      selectedValue
    );
    return isLearner;
  }

  onPhoneNumberSubmit() {
    if (!this.phoneNumberControl.valid) return;
    this.switchingStep = true;
    let phoneNumber = this.phoneNumberControl.value;
    // Add a country code if not present
    if (!phoneNumber.startsWith('+')) {
      const countryCode = '+49'; // Default to Germany
      if (phoneNumber.startsWith('0')) {
        phoneNumber = phoneNumber.substring(1);
      }
      phoneNumber = countryCode + phoneNumber;
      // Remove spaces, dashes, and parentheses
      phoneNumber = phoneNumber.replace(/[^0-9\+]/g, '');
      phoneNumber = phoneNumber.replace(/\s/g, '');
    }
    this.usersService
      .updateUser({
        id: this.auth.currentUserInfo$.value.sub,
        body: {
          id: this.auth.currentUserInfo$.value.sub,
          phone_number: phoneNumber,
        },
      })
      .subscribe({
        next: () => {
          this.nextStep();
        },
        error: (err) => {
          this.snackBar.open(err.error.message, null, {
            duration: 10000,
          });
          this.switchingStep = false;
        },
      });
  }

  activeGuidTour(skipTour = false) {
    if (skipTour) {
      this.stepsSelectValues.fourthStep.selectedValue.onActivate();
    } else {
      this.stepsSelectValues.fourthStep.selectedValue.skipTour();
    }
    this.dialogRef.close();
  }

  activateUseCase(useCase: UseCase) {
    this.switchingStep = true;
    this.stepsSelectValues.fourthStep.selectedValue = useCase;
    this.userConfig
      .set(
        'onboarding.selectedUseCase',
        getNestedValue(this.germanTranslations, useCase.title[this.edition])
      )
      .then(() => {
        this.getCardData();
        this.setStepsInfo();
        this.handleAmplitudeValues();
        this.nextStep();
      });
  }

  getCardData() {
    this.userJourney.initFlags().then(() => {
      switch (this.stepsSelectValues.fourthStep.selectedValue.amplitudeValue) {
        case 'course':
          const courseData = getSteps(TourTrackType.COURSE, this.userJourney);
          this.cardData = {
            tourType: TourTrackType.COURSE,
            title: 'dashboardNew.courseCard.title',
            icon: 'collections_bookmark',
            steps: courseData.steps,
            progress: courseData.progressData,
          };
          break;
        case 'liveEvent':
          const liveEventData = getSteps(
            TourTrackType.LIVE_EVENT,
            this.userJourney
          );
          this.cardData = {
            tourType: TourTrackType.LIVE_EVENT,
            title: 'dashboardNew.liveEventCard.title',
            icon: 'live_tv',
            steps: liveEventData.steps,
            progress: liveEventData.progressData,
          };
          break;
        case 'exam':
          const examData = getSteps(TourTrackType.EXAM, this.userJourney);
          this.cardData = {
            tourType: TourTrackType.EXAM,
            title: 'dashboardNew.examCard.title',
            icon: 'assignment',
            steps: examData.steps,
            progress: examData.progressData,
          };
          break;
      }
    });
  }

  async handleAmplitudeValues() {
    const amplitudeValues = this.getAmplitudeValues();
    if (amplitudeValues) {
      this.trackingService.trackEvent({
        event_type: UIEventEnum.ONBOARDING_FINISHED,
        event_properties: amplitudeValues,
      });
      await this.userConfig.set('qa.onboarding.seen', true);
    }
  }

  getAmplitudeValues() {
    const amplitudeValues = {
      role: null,
      knowledge_source: null,
      goals: null,
      selected_use_case: null,
    };
    Object.keys(this.stepsSelectValues).forEach((key) => {
      if (this.stepsSelectValues[key]) {
        let amplitudeValue = null;
        const selectedVal = this.stepsSelectValues[key]?.selectedValue;
        if (selectedVal) {
          if (Array.isArray(selectedVal)) {
            amplitudeValue = selectedVal.map((value) => value.amplitudeValue);
          } else {
            amplitudeValue = selectedVal.amplitudeValue;
          }
        }
        amplitudeValues[this.stepsSelectValues[key].id] = amplitudeValue;
      }
    });
    return amplitudeValues;
  }

  skipOnboarding() {
    this.dialog
      .confirm({
        title: this.translator.instant('onboarding.skipOnboarding'),
        text: this.translator.instant('onboarding.skipOnboardingConfirm'),
      })
      .subscribe((confirmed) => {
        if (!confirmed) return;
        this.userConfig.set('qa.onboarding.skipped', true).then(() => {
          this.dialogRef.close({ skipped: true });
        });
      });
  }
}
